import { useState, type ReactNode } from 'react'

/**
 * Bloque plegable sobre `<details>`/`<summary>` nativos — sin JS de apertura
 * propio, así el teclado del navegador sigue funcionando. Lo usa `/data` para
 * lo que el jugador consulta de vez en cuando (cuestionarios, perfil, detalle
 * de números) sin que ocupe la página entera todo el tiempo.
 *
 * El contenido se MONTA recién al abrir (y se desmonta al cerrar): adentro
 * hay gráficos de Chart.js, que si se montan en un `<details>` cerrado miden
 * un canvas de 0×0 y quedan en blanco cuando el bloque se abre.
 */
export function Collapsible({
  title,
  hint,
  defaultOpen = false,
  children,
}: {
  title: string
  /** Texto corto a la derecha del título (estado, conteo). */
  hint?: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="group rounded-md border border-outline bg-surface-elevated"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-md px-lg py-md">
        <span className="font-display text-sm font-bold uppercase tracking-wide text-text-primary">
          {title}
        </span>
        <span className="flex items-center gap-sm text-xs text-text-muted">
          {hint}
          <svg
            aria-hidden
            viewBox="0 0 12 12"
            className="h-3 w-3 transition-transform group-open:rotate-180"
          >
            <path
              d="M2 4.5 6 8.5 10 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </summary>
      {open && <div className="border-t border-outline px-lg py-md">{children}</div>}
    </details>
  )
}
